// Thin fetch wrapper mirroring the old static/app.js fetchJson: same-origin,
// cookie-authenticated, with a hard redirect to /login on 401.
import type {
  AgentSignalsResponse,
  AlarmsResponse,
  AuthStatusResponse,
  BootstrapResponse,
  ChartResponse,
  CompanionAgentsResponse,
  CompanionStatusResponse,
  EmailAlarm,
  EventAlarm,
  EventAlarmsResponse,
  PriceAlarm,
  PriceAlarmsResponse,
  SymbolResponse,
} from "./types";

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

const LOGIN_REDIRECT = "/login?next=/app";

export async function fetchJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      Accept: "application/json",
      ...(options.headers || {}),
    },
  });

  if (response.status === 401) {
    window.location.assign(LOGIN_REDIRECT);
    throw new ApiError("Authentication required.", 401);
  }

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = (payload as { detail?: string }).detail || "Request failed.";
    throw new ApiError(detail, response.status);
  }

  return payload as T;
}

export const api = {
  bootstrap: () => fetchJson<BootstrapResponse>("/api/app/bootstrap"),

  symbol: (symbol: string) =>
    fetchJson<SymbolResponse>(`/api/app/symbol/${encodeURIComponent(symbol)}`),

  chart: (symbol: string, range: string) =>
    fetchJson<ChartResponse>(
      `/api/app/chart/${encodeURIComponent(symbol)}?range=${encodeURIComponent(range)}`,
    ),

  companionStatus: () => fetchJson<CompanionStatusResponse>("/api/app/agents/status"),

  companionAgents: (symbol: string) =>
    fetchJson<CompanionAgentsResponse>(`/api/app/agents/${encodeURIComponent(symbol)}`),

  companionRefresh: (symbol: string, adminToken: string) =>
    fetchJson<CompanionAgentsResponse>(
      `/api/app/agents/${encodeURIComponent(symbol)}/refresh`,
      {
        method: "POST",
        headers: { "X-Admin-Token": adminToken },
      },
    ),

  authStatus: () => fetchJson<AuthStatusResponse>("/api/auth/status"),

  agentSignals: (symbol: string) =>
    fetchJson<AgentSignalsResponse>(`/api/app/signals/${encodeURIComponent(symbol)}`),

  alarms: () => fetchJson<AlarmsResponse>("/api/app/alarms"),

  createAlarm: (symbol: string, agentKey: string) =>
    fetchJson<EmailAlarm>("/api/app/alarms", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ symbol, agent_key: agentKey }),
    }),

  deleteAlarm: (id: number) =>
    fetchJson<{ status: string }>(`/api/app/alarms/${id}`, { method: "DELETE" }),

  eventAlarms: () => fetchJson<EventAlarmsResponse>("/api/app/event-alarms"),

  createEventAlarm: (symbol: string, eventType: string) =>
    fetchJson<EventAlarm>("/api/app/event-alarms", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ symbol, event_type: eventType }),
    }),

  deleteEventAlarm: (id: number) =>
    fetchJson<{ status: string }>(`/api/app/event-alarms/${id}`, { method: "DELETE" }),

  priceAlarms: () => fetchJson<PriceAlarmsResponse>("/api/app/price-alarms"),

  createPriceAlarm: (symbol: string, targetPrice: number) =>
    fetchJson<PriceAlarm>("/api/app/price-alarms", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ symbol, target_price: targetPrice }),
    }),

  rearmPriceAlarm: (id: number) =>
    fetchJson<PriceAlarm>(`/api/app/price-alarms/${id}/rearm`, { method: "POST" }),

  deletePriceAlarm: (id: number) =>
    fetchJson<{ status: string }>(`/api/app/price-alarms/${id}`, { method: "DELETE" }),
};
